import React, { useState,useEffect, useCallback, useMemo } from 'react';
import { Button, Box, Typography, FormControl, FormLabel, RadioGroup, Radio, FormControlLabel } from '@mui/material';
import { DateRangePicker, defaultStaticRanges } from 'react-date-range';
import {addDays, subMonths, addMonths, startOfMonth, endOfMonth, isSameDay } from 'date-fns';
import CommonPopover from '../../common/popover/CommonPopover';
import { DATE_FILTER_OPTIONS } from './constants';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import DateRangeOutlinedIcon from "@mui/icons-material/DateRangeOutlined";
import { DaysList } from '../../navbar/sideNavbar/FilterComponents/CalendarRangeOption';
import moment from 'moment';
import { categorizeDateRange } from '../../helpers/helperFunctions';

interface DateRangeFilterProps {
  onChange: (range: { startDate: Date; endDate: Date; dateFilterType: string }) => void;
  defaultVal?: {
    startDate: Date;
    endDate: Date;
    key : string;
  };
}

export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({ defaultVal , onChange }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [dateFilterType, setDateFilterType] = useState(defaultVal?.key || "1");
  const [rangeState, setRangeState] = useState([
    defaultVal ??
    {
      startDate: subMonths(new Date(), 1),
      endDate: addDays(new Date(), 0),
      key: "selection",
    }
  ]);

  const open = Boolean(anchorEl);

  const handleOpen = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleRangeChange = useCallback((ranges: any) => {
    const key = Object.keys(ranges)[0];
    setRangeState([ranges[key]]);
  }, []);

  const handleTypeChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setDateFilterType(event.target.value);
  }, []);

  const handleApply = useCallback(() => {
    handleClose();
    onChange({
      startDate: rangeState[0].startDate,
      endDate: rangeState[0].endDate,
      dateFilterType,
    });
  }, [rangeState, dateFilterType, onChange, handleClose]);

  useEffect(()=>{
    if (defaultVal) {
      setRangeState([defaultVal]);
    }
  },[defaultVal])

  // Define the "Next Month" static range
  const nextMonthRange = {
    label: 'Next Month',
    range: () => ({
      startDate: startOfMonth(addMonths(new Date(), 1)),
      endDate: endOfMonth(addMonths(new Date(), 1)),
    }),
    isSelected: (currentRange:any) => {
      const definedRange = nextMonthRange.range();
      return (
        isSameDay(currentRange.startDate, definedRange.startDate) &&
        isSameDay(currentRange.endDate, definedRange.endDate)
      );
    },
  };
  return (
    <>
      <Typography onClick={handleOpen} variant="subtitle2" className="filter-date-range-label cursor-pointer">
        {/* From {rangeState[0].startDate.toLocaleDateString()} To {rangeState[0].endDate.toLocaleDateString() } ({dateFilterLabel(`${dateFilterType}`)}) */}
        From <b>{moment(rangeState?.[0]?.["startDate"])?.format("L")} </b> (
                  {DaysList[rangeState?.[0]?.["startDate"]?.getDay()]}) to{" "}
                  <b> {moment(rangeState?.[0]?.["endDate"])?.format("L")}</b> (
                  {DaysList[rangeState?.[0]?.["endDate"]?.getDay()]}){"("}
                  {categorizeDateRange(
                    rangeState?.[0]?.["startDate"],
                    rangeState?.[0]?.["endDate"]
                  ) || ""}
                  {")"}
        <DateRangeOutlinedIcon />
      </Typography>
      <CommonPopover
        id="filterPopoverDateRange"
        open={open}
        anchorEl={anchorEl}
        className="filter-date-picker"
        handleClose={handleClose}
        bodyContent={
          <Box sx={{ p: 2 }}>
            <DateRangePicker
              onChange={handleRangeChange}
              moveRangeOnFirstSelection={true}
              months={2}
              ranges={rangeState}
              direction="horizontal"
              staticRanges={[
                ...defaultStaticRanges, // Keep the default presets like 'Today', 'Last Month', etc.
                nextMonthRange,        // Add your custom preset
              ]}
            />
            <Box sx={{ mt: 2 }}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Filter based on:</FormLabel>
                <RadioGroup
                  row
                  value={dateFilterType}
                  onChange={handleTypeChange}
                >
                  {DATE_FILTER_OPTIONS.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={<Radio size="small" />}
                      label={option.label}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Box>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button variant="outlined" onClick={handleClose} size="small">
                Cancel
              </Button>
              <Button variant="contained" onClick={handleApply} size="small">
                Apply
              </Button>
            </Box>
          </Box>
        }
      />
    </>
  );
};